/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Notification from "../common/Notification";
import PropTypes from "prop-types";

import {
  Button,
  IconButton,
  Grid,
  MenuItem,
  Dialog,
  InputLabel,
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";

import StyledTextField from "../common/StyledTextField";
import StyledFormControl from "../common/StyledFormControl";
import StyledSelect from "../common/StyledSelect";
import types from "../../helpers/types";
import CarrierOrganizationAutoComplete from "../common/CarrierOrganizationAutoComplete";
import { postOrganization, putCarrier } from "../../api/api";
import { fetchCarriers } from "../../slices/carrierManagementSlice";
import { fetchOrganizations } from "../../slices/organizationsSlice";

const AddNewCarrierModal = ({ handleClose, open }) => {
  const dispatch = useDispatch();

  const [tierType, setTierType] = useState("");
  const [contactName, setContactName] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [putCarrierRequestStatus, setPutCarrierRequestStatus] = useState(
    types.requestStatuses.initial
  );
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [
    postNewOrganizationRequestStatus,
    setPostNewOrganizationRequestStatus,
  ] = useState(types.requestStatuses.initial);
  const handleTierTypeChange = (event) => {
    const tier = Number(event.target.value);
    setTierType(tier);
    setValidationErrors({ ...validationErrors, tierType: false });
  };

  const handleCarrierNameChange = (event) => {
    let inputValue = event.target.value;
    inputValue = inputValue.replace(/\s\s+/g, " ").trimStart();
    setCarrierName(inputValue);
    setValidationErrors({
      ...validationErrors,
      carrierName: false,
      carrierNameTaken: false,
    });
  };

  const handleContactNameChange = (event) => {
    setContactName(event.target.value);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  const handleOrganizationChange = (selection) => {
    setSelectedOrganization(selection);
  };

  const handleCarrierCreation = async (event) => {
    const validation = {
      ...validationErrors,
    };
    if (!tierType) {
      validation.tierType = true;
    }

    if (!carrierName) {
      validation.carrierName = true;
    }

    setValidationErrors(validation);
    const isValid = Object.values(validation).every((value) => !value);
    if (isValid) {
      let organizationId = selectedOrganization?.id;
      if (
        selectedOrganization &&
        selectedOrganization.label &&
        !selectedOrganization.id
      ) {
        setPostNewOrganizationRequestStatus(types.requestStatuses.inProgress);
        try {
          const { data } = await postOrganization(selectedOrganization.label);
          setPostNewOrganizationRequestStatus(types.requestStatuses.success);
          organizationId = data;
          dispatch(fetchOrganizations());
        } catch (e) {
          setPostNewOrganizationRequestStatus(types.requestStatuses.failure);
        }
      }

      try {
        setPutCarrierRequestStatus(types.requestStatuses.inProgress);
        const newCarrier = {
          carrierName: carrierName.trim(),
          contactFullName: contactName,
          carrierEmail: email,
          carrierPhone1: phoneNumber,
          carrierTier: tierType,
          organizationsId: organizationId ? organizationId : 0,
        };
        await putCarrier(newCarrier);
      } catch (error) {
        const { response } = error;
        if (response.status === 400) {
          setValidationErrors({ ...validationErrors, carrierNameTaken: true });
        }
        setPutCarrierRequestStatus(types.requestStatuses.failure);
        return;
      }
      setPutCarrierRequestStatus(types.requestStatuses.success);
      handleClose();
      setShowSuccessNotification(true);
      dispatch(fetchCarriers());
    }
  };

  useEffect(() => {
    setCarrierName("");
    setContactName("");
    setEmail("");
    setPhoneNumber("");
    setTierType("");
    setSelectedOrganization(null);
    setValidationErrors({});
  }, [open]);

  const isAddingNewCarrier =
    putCarrierRequestStatus === types.requestStatuses.inProgress ||
    postNewOrganizationRequestStatus === types.requestStatuses.inProgress;

  return (
    <>
      {open && (
        <Dialog
          onClose={handleClose}
          open={true}
          maxWidth="md"
          fullWidth={true}
          disableBackdropClick={true}
          data-testid="add-carrier-modal"
        >
          <MuiDialogTitle>
            Add New Carrier
            <IconButton style={{ float: "right" }} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </MuiDialogTitle>
          <MuiDialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                Please provide the basic information below to add a carrier to
                RouteConnect™ Tracking and RouteConnect™ Available Routes.
                <br />
                *Carrier name must match existing EPTS carrier name.*
                <br />
                *After adding this carrier, a user will have to be associated
                with the carrier in User Management.*
              </Grid>

              <>
                <Grid item xs={4}>
                  <StyledTextField
                    onChange={handleCarrierNameChange}
                    label="Carrier name (required)"
                    variant="outlined"
                    value={carrierName}
                    fullWidth={true}
                    error={
                      validationErrors.carrierName ||
                      validationErrors.carrierNameTaken
                    }
                    helperText={
                      validationErrors.carrierNameTaken
                        ? "This carrier already exists in RouteConnect™."
                        : ""
                    }
                    inputProps={{
                      "data-testid": "carrier-name-textfield",
                      maxLength: 256,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <StyledFormControl
                    variant="outlined"
                    fullWidth={true}
                    error={validationErrors.tierType}
                  >
                    <InputLabel id="user-type">Tier (required)</InputLabel>
                    <StyledSelect
                      value={tierType}
                      label="Tier (required)"
                      labelId="tier-type"
                      onChange={handleTierTypeChange}
                      data-testid="tier-type-select"
                    >
                      <MenuItem value={types.tiers.tier1}>Tier 1</MenuItem>
                      <MenuItem value={types.tiers.tier2}>Tier 2</MenuItem>
                      <MenuItem value={types.tiers.tier3}>Tier 3</MenuItem>
                      <MenuItem value={types.tiers.doNotUse}>
                        Do Not Use
                      </MenuItem>
                    </StyledSelect>
                  </StyledFormControl>
                </Grid>
                <Grid item xs={4}>
                  <CarrierOrganizationAutoComplete
                    selectedOrganization={selectedOrganization}
                    setSelectedOrganization={handleOrganizationChange}
                    searchIconPosition="260px"
                  />
                </Grid>
                <Grid item xs={4}>
                  <StyledTextField
                    onChange={handleContactNameChange}
                    label="Contact name"
                    variant="outlined"
                    value={contactName}
                    fullWidth={true}
                    inputProps={{
                      "data-testid": "contact-name-textfield",
                      maxLength: 256,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <StyledTextField
                    onChange={handleEmailChange}
                    label="Email"
                    variant="outlined"
                    value={email}
                    fullWidth={true}
                    inputProps={{
                      "data-testid": "email-textfield",
                      maxLength: 100,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <StyledTextField
                    onChange={handlePhoneNumberChange}
                    label="Phone"
                    variant="outlined"
                    value={phoneNumber}
                    fullWidth={true}
                    inputProps={{
                      "data-testid": "phone-textfield",
                      maxLength: 15,
                    }}
                  />
                </Grid>
              </>
            </Grid>
          </MuiDialogContent>
          <MuiDialogActions>
            <Button
              variant="contained"
              onClick={handleCarrierCreation}
              disabled={isAddingNewCarrier}
              data-testid="add-carrier-button"
            >
              Add Carrier
            </Button>
          </MuiDialogActions>
        </Dialog>
      )}
      <Notification
        message="New carrier added."
        open={showSuccessNotification}
        setOpen={setShowSuccessNotification}
      />
    </>
  );
};

AddNewCarrierModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default AddNewCarrierModal;
